import { type FC, useMemo } from 'react';
import { Outlet } from 'react-router-dom';
import { Controller, useFormContext } from 'react-hook-form';
import { QStack } from '../../../../QLayouts/Stack/Stack';
import { QBox } from '../../../../QLayouts/Box/Box';
import { useQResourceContext } from '../../../../QLayouts/QResourceRoutes/context';
import { QDataTable } from '../../../Table/DataTable';
import { createQColumnHelper, DataProvider } from '../../..//Table';
import { ResolvedTask, TaskStatus, TaskStatusToStatusConfigMap } from './types';
import { QText } from '../../../../QAtoms/Text/Text';
import { QPageLoader } from '../../../../QMolecules/PageLoader/PageLoader';
import { QFormControl } from '../../../../QAtoms/FormControl/FormControl';
import { QSelect, QSelectPlaceholder } from '../../../../QAtoms/Select';
import { addDays, fromUnixTime } from 'date-fns';
import { QResourceLabel } from '../../../../QMolecules/ResourceLabel/ResourceLabel';
import { useRetrieveTaskList } from './useRetrieveTaskList';
import { useUsers } from './useUsers';
import { SearchDomain } from '../../../../types/domains/Search';
import * as Filtering from '../../../Table/Filtering';

const cH = createQColumnHelper<ResolvedTask>();

// Filter definitions for assignee and status filters
const taskFilterDefinitions = {
  assignee: {
    label: 'Assignee',
    schema: Filtering.schemas.NumberSchema(),
  },
  status: {
    label: 'Status',
    schema: Filtering.schemas.StringSchema(),
    defaultValue: TaskStatus.OPEN,
  },
} satisfies Filtering.FilterDefinitions;

// Status options for the dropdown
const statusOptions = [
  {
    label: TaskStatusToStatusConfigMap[TaskStatus.OPEN].label,
    value: TaskStatus.OPEN,
  },
  {
    label: TaskStatusToStatusConfigMap[TaskStatus.CLOSED_SUCCESS].label,
    value: TaskStatus.CLOSED_SUCCESS,
  },
  {
    label: TaskStatusToStatusConfigMap[TaskStatus.CLOSED_FAIL].label,
    value: TaskStatus.CLOSED_FAIL,
  },
];

// Client-side filter function
const filterTasksByFilters = (
  task: ResolvedTask,
  searchTerm: string | undefined,
  filters: Filtering.ResolvedFilters<Filtering.FilterDefinitions> | undefined,
): boolean => {
  // Filter by assignee (match creator_id)
  if (filters?.assignee?.value != null) {
    if (task.creator_id !== filters.assignee.value) {
      return false;
    }
  }

  // Filter by status (match status enum)
  if (filters?.status?.value) {
    if (task.status !== filters.status.value) {
      return false;
    }
  }

  return true;
};

const getPathname = (url: string | null) => {
  if (!url) {
    return undefined;
  }
  try {
    return new URL(url).pathname;
  } catch (error) {
    console.error('Invalid URL', error);
    return undefined;
  }
};

// TaskFilters component - renders inline filter dropdowns (without wrapping CustomFormContent)
const TaskFiltersContent: FC = () => {
  const { data: usersData } = useUsers();
  const { control } = useFormContext();
  const { setFilters } = Filtering.useFiltering<typeof taskFilterDefinitions>();

  const assigneeOptions = useMemo(() => {
    if (!usersData?.users) {
      return [];
    }
    return usersData.users.map((user) => ({
      label: user.full_name,
      value: user.user_id,
    }));
  }, [usersData?.users]);

  const handleAssigneeChange = (selected: { value: number } | null) => {
    const newValue = selected?.value ?? null;
    setFilters({ assignee: newValue });
  };

  const handleStatusChange = (selected: { value: string } | null) => {
    const newValue = selected?.value ?? null;
    setFilters({ status: newValue });
  };

  return (
    <QStack direction="row" spacing={4} alignItems="flex-end">
      <Controller
        name="assignee"
        control={control}
        render={({ field: { onChange, value } }) => (
          <QBox width="250px">
            <QFormControl data-cy="task-assignee-filter">
              <QSelect
                options={assigneeOptions}
                value={value}
                onChange={(selected) => {
                  onChange(selected?.value ?? null);
                  handleAssigneeChange(selected);
                }}
                isClearable
              >
                <QSelectPlaceholder>
                  <QText as="span">All assignees</QText>
                </QSelectPlaceholder>
              </QSelect>
            </QFormControl>
          </QBox>
        )}
      />

      <Controller
        name="status"
        control={control}
        defaultValue={TaskStatus.OPEN}
        render={({ field: { onChange, value } }) => (
          <QBox width="200px">
            <QFormControl data-cy="task-status-filter">
              <QSelect
                options={statusOptions}
                value={value}
                onChange={(selected) => {
                  onChange(selected?.value ?? null);
                  handleStatusChange(selected);
                }}
                isClearable
              >
                <QSelectPlaceholder>
                  <QText as="span">All statuses</QText>
                </QSelectPlaceholder>
              </QSelect>
            </QFormControl>
          </QBox>
        )}
      />
    </QStack>
  );
};

export const TaskList: FC = () => {
  useQResourceContext();
  const { isLoading, tasks } = useRetrieveTaskList();

  const columns = useMemo(
    () => [
      cH.id((t) => `#T${t.id}`, {
        id: 'task-id',
        header: 'ID',
        minWidth: '80px',
        maxWidth: '120px',
        weight: 1,
      }),
      cH.textLink('title', (task) => `${task.id}`, {
        id: 'task-title',
        minWidth: '200px',
        weight: 3,
        'data-cy': (t) => `task-tab-title-${t.id}`,
      }),
      cH.text(() => 'Compliance', {
        id: 'type',
        header: 'Task type',
        minWidth: '80px',
        maxWidth: '160px',
        weight: 1,
      }),
      cH.custom((t) => t.source, {
        id: 'source',
        header: 'Source',
        minWidth: '200px',
        weight: 2,
        cell: ({ row }) => (
          <QResourceLabel
            openInNewTab
            link={getPathname(row.original.source.link)}
            domain={row.original.source.domain as SearchDomain}
          >
            <QText noOfLines={2}>{row.original.source.label}</QText>
          </QResourceLabel>
        ),
      }),
      cH.text('assignee', {
        id: 'assignee',
        header: 'Assignee',
        minWidth: '80px',
        maxWidth: '160px',
        weight: 1,
      }),
      cH.date((t) => addDays(fromUnixTime(t.created_at), t.time_limit), {
        id: 'due-date',
        header: 'Due Date',
        width: '120px',
        includeTime: false,
        highlightPastDates: (t) => t.status === TaskStatus.OPEN,
      }),
      cH.status((t) => TaskStatusToStatusConfigMap[t.status].label, {
        id: 'task-status',
        header: 'Status',
        width: '120px',
        statuses: {
          Open: 'blue',
          Canceled: 'red',
          Completed: 'green',
        } as const,
      }),
    ],
    [],
  );

  return (
    <Filtering.FilterProvider
      definitions={taskFilterDefinitions}
      searchTermKey={null}
    >
      <QStack spacing={4} p={4}>
        <QText>
          Use this overview to track open work items and monitor task progress.
        </QText>
        {isLoading && <QPageLoader />}
        {!isLoading && (
          <DataProvider.FixedWithAutoPagination
            data={tasks}
            filterOption={filterTasksByFilters}
          >
            <QDataTable columns={columns} data-cy="task-table">
              <Filtering.CustomFormContent>
                <TaskFiltersContent />
              </Filtering.CustomFormContent>
            </QDataTable>
          </DataProvider.FixedWithAutoPagination>
        )}
        <Outlet />
      </QStack>
    </Filtering.FilterProvider>
  );
};

TaskList.displayName = 'TaskList';
